import React from 'react';

import type { VoiceRecorderProps } from '../VoiceRecorder';
import type { AudioDropFileData } from './types';
import type { VoiceNoteStep, VoiceScriptRequest } from './VoiceNoteScriptPicker';
import { cn } from '../cn';
import Button from '../Input2/Button';
import ButtonGroup from '../Input2/ButtonGroup';
import FilterSelect from '../Input2/FilterSelect';
import Textarea from '../Input2/Textarea';
import { StepIconSvg } from '../StepIcon';
import { Arrows, Editor, General, Media } from '../StrokeIcons';
import { getTextFromHtml } from '../utils/htmlUtils';
import { VoiceRecorder } from '../VoiceRecorder';
import { WaveSpinner } from '../WaveSpinner';
import { VoiceNoteLeadStep, VoiceNoteStepper, VoiceNoteTakesStep } from './VoiceNoteScriptPicker';

// ============================================================================
// SeparatorWithPlusIcon
// ============================================================================

function SeparatorWithPlusIcon() {
  return (
    <div className="flex flex-col items-center relative z-10">
      <div className="flex items-center justify-center size-5 shrink-0 absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2 rounded-full text-indigo-500 bg-indigo-100 dark:bg-indigo-900">
        <General.PlusCircle className="size-4" />
      </div>
      <div className="w-full h-px bg-indigo-300 dark:bg-indigo-700" />
    </div>
  );
}

// ============================================================================
// Types
// ============================================================================

export type VoiceNoteActionCardPureProps = {
  audioDropFiles: AudioDropFileData[];
  audioDropFilesIsLoading: boolean;
  onGenerateVoiceNote?: (
    text: string,
    voiceDropId?: string,
  ) => Promise<{ stepId: string; audioUrl: string; audioId: string } | undefined>;
  onApproveVoiceNote?: (stepId: string) => Promise<void>;
  /** Called when audio generation completes with URL so component can auto-play */
  onAudioGenerated?: (publicUrl: string, audioId: string) => void;
  onRecordTrigger?: () => void;
  /** Called when user records a voice note - handles upload to server and auto-play.
      May be async; the card awaits it and closes itself once it resolves. */
  onRecordingComplete?: (audioBlob: Blob, durationMs: number) => void | Promise<void>;
  isUploadingRecording?: boolean;
  /** Dossier talking points, best first — offered as "lead with" chips in the script picker. */
  voiceAngles?: string[];
  /** Write up to three SPOKEN takes for the picker from the chosen angle and/or a typed steer.
      Absent → no picker, no wand; the rep types the message themselves. */
  onGenerateVoiceScripts?: (request: VoiceScriptRequest) => Promise<string[]>;
  /** Optional replacement for the built-in `VoiceRecorder`. When provided, this component
      is rendered in record mode instead. Used by the Chrome extension to route mic capture
      through an offscreen document (side-panel `getUserMedia` is unreliable). */
  voiceRecorderComponent?: React.ComponentType<VoiceRecorderProps>;
};

// ============================================================================
// AppendClipSelect — "Select a voice clip to append…" + remove
// ============================================================================

type AppendClipSelectProps = {
  selectedFileId: string | null;
  setSelectedFileId: (value: string | null) => void;
  playingPreviewClipId: string | null;
  setPlayingPreviewClipId: (value: string | null) => void;
  audioDropFiles: AudioDropFileData[];
  disabled: boolean;
  previewAudioRef: React.MutableRefObject<HTMLAudioElement | null>;
};

function AppendClipSelect({
  selectedFileId,
  setSelectedFileId,
  playingPreviewClipId,
  setPlayingPreviewClipId,
  audioDropFiles,
  disabled,
  previewAudioRef,
}: AppendClipSelectProps) {
  return (
    <div className="grid grid-rows-[1fr] opacity-100 overflow-hidden">
      <div className="overflow-hidden flex items-center">
        {audioDropFiles.length > 0 ? (
          <FilterSelect
            className="flex-1 min-w-0"
            color="blue"
            tone="outline"
            shape="square"
            bordered={false}
            icon={<Media.Microphone01 />}
            placeholder="Select a voice clip to append..."
            options={audioDropFiles.map((clip) => ({
              value: clip.id,
              label: clip.name,
              action: {
                icon:
                  playingPreviewClipId === clip.id ? (
                    <Media.PauseCircle className="size-5" />
                  ) : (
                    <Media.PlayCircle className="size-5" />
                  ),
                onClick: (value: string) => {
                  const clipToPlay = audioDropFiles.find((c) => c.id === value);
                  if (!clipToPlay?.url) return;

                  if (playingPreviewClipId === value) {
                    previewAudioRef.current?.pause();
                    setPlayingPreviewClipId(null);
                  } else {
                    previewAudioRef.current?.pause();
                    const audio = new Audio(clipToPlay.url);
                    previewAudioRef.current = audio;
                    audio.onended = () => setPlayingPreviewClipId(null);
                    audio.onerror = () => setPlayingPreviewClipId(null);
                    void audio.play().catch(() => {});
                    setPlayingPreviewClipId(value);
                  }
                },
                className: cn(
                  'size-7 rounded-full transition-colors flex items-center justify-center',
                  playingPreviewClipId === clip.id
                    ? 'bg-indigo-500 text-white'
                    : 'bg-indigo-100 text-indigo-600 hover:bg-indigo-200 dark:bg-indigo-900/50 dark:text-indigo-400 dark:hover:bg-indigo-800',
                ),
              },
            }))}
            value={selectedFileId ?? ''}
            onChange={(value) => {
              setSelectedFileId(value);
              previewAudioRef.current?.pause();
              setPlayingPreviewClipId(null);
            }}
            disabled={disabled}
          />
        ) : (
          <p className="text-sm text-indigo-600 dark:text-indigo-400 flex-1">No voice notes uploaded yet.</p>
        )}
        {selectedFileId && (
          <Button
            size="sm"
            color="gray"
            text
            data-testid="remove-appended-voice-clip"
            tooltip="Remove appended voice clip"
            disabled={disabled}
            onClick={() => {
              setSelectedFileId(null);
              previewAudioRef.current?.pause();
              previewAudioRef.current = null;
              setPlayingPreviewClipId(null);
            }}
            icon={<General.XMark className="size-4" />}
          />
        )}
      </div>
    </div>
  );
}

// ============================================================================
// VoiceNoteActionCardPure - Main Component
// ============================================================================

export function VoiceNoteActionCardPure({
  audioDropFiles,
  audioDropFilesIsLoading,
  onGenerateVoiceNote,
  onApproveVoiceNote,
  onAudioGenerated,
  onRecordTrigger,
  onRecordingComplete,
  isUploadingRecording,
  voiceAngles = [],
  onGenerateVoiceScripts,
  voiceRecorderComponent: RecorderComponent = VoiceRecorder,
}: VoiceNoteActionCardPureProps) {
  const [editing, setEditing] = React.useState(false);
  const [isGenerating, setIsGenerating] = React.useState(false);
  const [isSending, setIsSending] = React.useState(false);
  // Script picker: the takes from the last request, what they were asked for,
  // and the failure to show. Which take is "selected" is derived from the
  // message box — editing the text un-selects it, picking one re-selects it.
  const [isWritingScripts, setIsWritingScripts] = React.useState(false);
  const [scriptTakes, setScriptTakes] = React.useState<string[]>([]);
  const [activeAngle, setActiveAngle] = React.useState<string | null>(null);
  const [lastScriptRequest, setLastScriptRequest] = React.useState<VoiceScriptRequest | null>(null);
  const [scriptError, setScriptError] = React.useState<string | null>(null);
  const [selectedFileId, setSelectedFileId] = React.useState<string | null>(null);
  const [playingPreviewClipId, setPlayingPreviewClipId] = React.useState<string | null>(null);
  const [ttsMessageRaw, setTtsMessageRaw] = React.useState('');
  // Defensive: any value entering the TTS textarea that looks like HTML (`<br>`, `<p>`, etc.)
  // gets stripped to plain text. Catches paths where the AI/cadence layer returns TipTap HTML
  // and would otherwise render literal `<br><br>` tags as characters in the textarea.
  const ttsMessage = ttsMessageRaw;
  const setTtsMessage = React.useCallback((value: string) => {
    if (/<\w+[^>]*>|&\w+;/.test(value)) {
      // Paragraphs must stay separated by a space, or "feat.At Poseidon" happens.
      setTtsMessageRaw(getTextFromHtml(value, true));
      return;
    }
    setTtsMessageRaw(value);
  }, []);
  // Wizard position. `lead` and `script` exist only when scripts can be written;
  // otherwise the card opens straight on `deliver`, where the rep types and
  // chooses AI voice or their own.
  const [step, setStep] = React.useState<VoiceNoteStep>('deliver');
  const [deliverMode, setDeliverMode] = React.useState<'generate' | 'record'>('generate');
  const [voiceNoteStepId, setVoiceNoteStepId] = React.useState<string | null>(null);
  const [generatedAudioUrl, setGeneratedAudioUrl] = React.useState<string | null>(null);
  const [generatedAudioId, setGeneratedAudioId] = React.useState<string | null>(null);
  const [recordedAudioUrl, setRecordedAudioUrl] = React.useState<string | null>(null);
  const [recordedAudioBlob, setRecordedAudioBlob] = React.useState<{ blob: Blob; durationMs: number } | null>(null);
  const previewAudioRef = React.useRef<HTMLAudioElement | null>(null);
  const [hasPlayedGeneratedAudio, setHasPlayedGeneratedAudio] = React.useState(false);

  // When the TTS message or the appended clip changes, the previously generated
  // audio no longer matches what's on screen, so the user must re-listen before
  // sending. These resets are deliberately POST-COMMIT useEffects, not an
  // adjust-during-render prevRef: `hasPlayedGeneratedAudio` also has an *async*
  // writer (the generated-audio `onended` handler sets it true on playback end).
  // A during-render reset races that async setter and intermittently re-arms the
  // gate after an unrelated re-render; the effect runs after commit so the two
  // writers stay ordered. See VoiceNoteActionCardPure.cy.tsx "re-listen sync".
  const previousTtsMessage = React.useRef(ttsMessage);
  React.useEffect(() => {
    if (ttsMessage !== previousTtsMessage.current) {
      setHasPlayedGeneratedAudio(false);
      previousTtsMessage.current = ttsMessage;
    }
  }, [ttsMessage]);

  const previousSelectedFileId = React.useRef(selectedFileId);
  React.useEffect(() => {
    if (selectedFileId !== previousSelectedFileId.current) {
      setHasPlayedGeneratedAudio(false);
      previousSelectedFileId.current = selectedFileId;
    }
  }, [selectedFileId]);

  const needsToPlayAudio = !!generatedAudioId && !hasPlayedGeneratedAudio;

  const selectedTake = React.useMemo(() => {
    const index = scriptTakes.indexOf(ttsMessage);
    return index === -1 ? null : index;
  }, [scriptTakes, ttsMessage]);

  // Ask for three spoken takes and put the first one in the message box so the
  // rep can generate audio immediately; the others stay one click away.
  const handleWriteScripts = async (request: VoiceScriptRequest) => {
    if (!onGenerateVoiceScripts) return;
    setIsWritingScripts(true);
    setScriptError(null);
    setActiveAngle(request.angle ?? null);
    setLastScriptRequest(request);
    try {
      // Takes are plain spoken text; `setTtsMessage` already de-HTMLs anything that isn't.
      const takes = (await onGenerateVoiceScripts(request)).map((t) => t.trim()).filter(Boolean);
      setScriptTakes(takes);
      if (takes.length === 0) setScriptError('Nothing came back. Try another angle.');
    } catch {
      setScriptError("Couldn't write a script. Try again.");
    } finally {
      setIsWritingScripts(false);
    }
  };

  const pickTake = (index: number) => {
    const take = scriptTakes[index];
    if (take == null) return;
    setTtsMessage(take);
    setStep('deliver');
  };

  const canWriteScripts = onGenerateVoiceScripts != null;
  const firstStep: VoiceNoteStep = canWriteScripts ? 'lead' : 'deliver';

  const openAt = (at: VoiceNoteStep, mode: 'generate' | 'record' = 'generate') => {
    setEditing(true);
    setStep(at);
    setDeliverMode(mode);
  };

  const requestScripts = (request: VoiceScriptRequest) => {
    setStep('script');
    void handleWriteScripts(request);
  };

  // Where "back" goes from each step, given what exists.
  const previousStep: VoiceNoteStep | null =
    step === 'deliver' && canWriteScripts
      ? scriptTakes.length > 0 || isWritingScripts
        ? 'script'
        : 'lead'
      : step === 'script'
        ? 'lead'
        : null;
  const reachableSteps: VoiceNoteStep[] = canWriteScripts
    ? ([
        'lead',
        ...(scriptTakes.length > 0 ? (['script'] as const) : []),
        ...(ttsMessage.trim() ? (['deliver'] as const) : []),
      ] as VoiceNoteStep[])
    : [];

  const resetScripts = () => {
    setScriptTakes([]);
    setActiveAngle(null);
    setLastScriptRequest(null);
    setScriptError(null);
  };

  const handleGenerateAudio = async () => {
    setIsGenerating(true);
    // Stop any currently-playing preview before regenerating — otherwise the
    // previous draft keeps playing while the new one is being fetched and the
    // user hears the old voice + old appended clip on top of the new one.
    previewAudioRef.current?.pause();
    previewAudioRef.current = null;
    setPlayingPreviewClipId(null);
    setHasPlayedGeneratedAudio(false);
    try {
      const voiceDropId = selectedFileId ?? undefined;
      const result = await onGenerateVoiceNote?.(ttsMessage, voiceDropId);
      if (result?.audioUrl) {
        setVoiceNoteStepId(result.stepId);
        setGeneratedAudioUrl(result.audioUrl);
        setGeneratedAudioId(result.audioId);
        // Pass the fresh URL directly. State updates are async, so reading
        // `generatedAudioUrl` from inside `playGeneratedAudio` would still
        // see the previous draft and replay stale audio.
        playGeneratedAudio(result.audioUrl);
      }
      setIsGenerating(false);
    } catch {
      setIsGenerating(false);
    }
  };

  const handleApproveVoiceNote = async () => {
    if (!voiceNoteStepId) return;
    setIsSending(true);
    try {
      await onApproveVoiceNote?.(voiceNoteStepId);
      setEditing(false);
      setIsSending(false);
      setVoiceNoteStepId(null);
    } catch {
      setIsSending(false);
    }
  };

  // Record-mode send: hand the recorded blob to the consumer, then tear down the
  // preview + recorded state and close the card once it resolves. Without this the
  // card lingers in the "Recording ready" view after a successful send, which reads
  // as "it didn't send" even though it did. Mirrors
  // handleApproveVoiceNote's close-on-success behavior (parent shows its own toast).
  const handleSendRecording = async () => {
    if (!recordedAudioBlob || isSending) return;
    // Local guard closes the double-send window synchronously — the parent-driven
    // `isUploadingRecording` flips a tick later (async), so relying on it alone lets a
    // fast second click fire another upload. Mirrors handleApproveVoiceNote.
    setIsSending(true);
    try {
      await onRecordingComplete?.(recordedAudioBlob.blob, recordedAudioBlob.durationMs);
    } catch {
      // Consumer surfaces its own error toast; keep the recording so the user can retry.
      setIsSending(false);
      return;
    }
    previewAudioRef.current?.pause();
    previewAudioRef.current = null;
    setPlayingPreviewClipId(null);
    if (recordedAudioUrl) URL.revokeObjectURL(recordedAudioUrl);
    setRecordedAudioUrl(null);
    setRecordedAudioBlob(null);
    setIsSending(false);
    setDeliverMode('generate');
    setEditing(false);
  };

  // Play the generated audio + selected clip sequentially.
  // Accepts an optional override URL so callers that just regenerated a draft
  // can pass the fresh URL directly without waiting for state to flush.
  const playGeneratedAudio = (urlOverride?: string) => {
    const url = urlOverride ?? generatedAudioUrl;
    if (!url) return;

    if (playingPreviewClipId === 'generated' && !urlOverride) {
      previewAudioRef.current?.pause();
      previewAudioRef.current = null;
      setPlayingPreviewClipId(null);
      setHasPlayedGeneratedAudio(false);
    } else {
      previewAudioRef.current?.pause();
      const audio = new Audio(url);
      previewAudioRef.current = audio;

      // When generated audio ends, mark as played and chain to the voice clip if selected
      audio.onended = () => {
        setHasPlayedGeneratedAudio(true);
        onAudioGenerated?.(url, generatedAudioId ?? '');
        const selectedClip = audioDropFiles.find((f) => f.id === selectedFileId);
        if (selectedClip?.url) {
          const clipAudio = new Audio(selectedClip.url);
          previewAudioRef.current = clipAudio;
          clipAudio.onended = () => setPlayingPreviewClipId(null);
          clipAudio.onerror = () => setPlayingPreviewClipId(null);
          void clipAudio.play().catch(() => {});
        } else {
          setPlayingPreviewClipId(null);
        }
      };
      audio.onerror = () => {
        setPlayingPreviewClipId(null);
        setHasPlayedGeneratedAudio(false);
      };
      void audio.play().catch(() => {});
      setPlayingPreviewClipId('generated');
    }
  };

  const canPlayGenerated = generatedAudioUrl && (selectedFileId || generatedAudioId);

  // Auto-select newly generated audio when audioDropFiles changes (only if no file is selected)
  const previousFileCount = React.useRef(audioDropFiles.length);
  React.useEffect(() => {
    if (audioDropFiles.length > previousFileCount.current && audioDropFiles.length > 0) {
      // New file was added - select the most recent one, but only if user hasn't selected one
      const newFile = audioDropFiles[audioDropFiles.length - 1];
      if (newFile && !selectedFileId) {
        setSelectedFileId(newFile.id);
      }
    }
    previousFileCount.current = audioDropFiles.length;
  }, [audioDropFiles, selectedFileId]);

  if (!editing) {
    return (
      <div className="flex gap-1.5 flex-1">
        <Button
          className="flex-1"
          onClick={() => openAt(firstStep)}
          icon={<StepIconSvg iconType="voice_note" />}
          color="blue"
          tone="soft">
          <span>
            Send a{' '}
            <span className="font-medium text-blue-700 underline decoration-blue-300/50 underline-offset-2 group-hover/btn:text-white group-hover/btn:decoration-white/60 dark:text-blue-300 dark:decoration-blue-500/50">
              Voice Note
            </span>
          </span>
        </Button>
        {onRecordTrigger && (
          <Button
            tooltip="Record Audio Note"
            color="red"
            tone="soft"
            onClick={() => {
              onRecordTrigger?.();
              openAt('deliver', 'record');
            }}
            icon={<Media.Microphone01 />}
          />
        )}
      </div>
    );
  }

  const onCancel = () => {
    // Stop any preview that's still playing so closing the card doesn't leave
    // audio playing in the background.
    previewAudioRef.current?.pause();
    previewAudioRef.current = null;
    setPlayingPreviewClipId(null);
    setHasPlayedGeneratedAudio(false);
    setEditing(false);
    setStep(firstStep);
    setDeliverMode('generate');
    resetScripts();
    // Clean up recorded audio
    if (recordedAudioUrl) {
      URL.revokeObjectURL(recordedAudioUrl);
      setRecordedAudioUrl(null);
      setRecordedAudioBlob(null);
    }
  };

  const isRecordMode = step === 'deliver' && deliverMode === 'record';
  const title =
    step === 'lead'
      ? 'What do you want to lead with?'
      : step === 'script'
        ? 'Pick a take'
        : isRecordMode
          ? 'Record a voice note'
          : 'Send a voice note';

  return (
    <>
      <div
        className={cn(
          'flex flex-col border rounded-lg overflow-hidden',
          !isRecordMode && 'border-indigo-400 dark:border-indigo-500 bg-indigo-50 dark:bg-indigo-950',
          isRecordMode && 'border-red-400 dark:border-red-500 bg-red-50 dark:bg-red-950',
        )}>
        <div className="flex items-center gap-2 px-3 py-2">
          {previousStep != null ? (
            <Button
              size="sm"
              text
              color="gray"
              tooltip="Back"
              data-testid="voice-back"
              disabled={isGenerating || isSending}
              onClick={() => setStep(previousStep)}
              icon={<Arrows.ChevronLeft className="size-4" />}
            />
          ) : (
            <StepIconSvg iconType="voice_note" className="size-5 text-zumthor-600 shrink-0" />
          )}
          <span className="flex-1 min-w-0 truncate text-sm font-medium text-astronaut-700 dark:text-astronaut-300">
            {title}
          </span>
          {canWriteScripts && <VoiceNoteStepper step={step} reachable={reachableSteps} onStepClick={setStep} />}
          <Button
            size="sm"
            text
            color="gray"
            tooltip="Cancel"
            data-testid="voice-cancel"
            onClick={onCancel}
            disabled={isGenerating || isSending}
            icon={<General.XMark className="size-4" />}
          />
        </div>

        {audioDropFilesIsLoading && (
          <div className="flex items-center justify-center p-4">
            <WaveSpinner />
          </div>
        )}

        {!audioDropFilesIsLoading && step === 'lead' && (
          <VoiceNoteLeadStep
            angles={voiceAngles}
            activeAngle={activeAngle}
            disabled={isWritingScripts}
            onGenerate={requestScripts}
          />
        )}

        {!audioDropFilesIsLoading && step === 'script' && (
          <VoiceNoteTakesStep
            takes={scriptTakes}
            selectedTake={selectedTake}
            isGenerating={isWritingScripts}
            error={scriptError}
            onPickTake={pickTake}
            onRegenerate={lastScriptRequest ? () => void handleWriteScripts(lastScriptRequest) : undefined}
          />
        )}

        {!audioDropFilesIsLoading && step === 'deliver' && (
          <div className="flex flex-col">
            {/* The script. Editable in AI-voice mode; shown as a teleprompter in record mode. */}
            {!isRecordMode && (
              <Textarea
                value={ttsMessage}
                onChange={(e) => setTtsMessage(e.target.value)}
                placeholder={
                  canWriteScripts ? 'Type what to say, or go back and let it write…' : 'Type your message here...'
                }
                rows={4}
                bordered={false}
                color="blue"
                tone="outline"
                shape="square"
                className="w-full"
              />
            )}

            {/* How the audio gets made. Only offered when the rep can actually record. */}
            {onRecordingComplete && (
              <div className="px-3 py-2">
                <ButtonGroup
                  size="sm"
                  shape="pill"
                  color={isRecordMode ? 'red' : 'blue'}
                  tone="outline"
                  equalWidth
                  className="w-full">
                  <Button
                    tooltip="Synthesize the script in your cloned voice"
                    active={!isRecordMode}
                    data-testid="voice-mode-generate"
                    icon={<Editor.Wand className="size-4" />}
                    onClick={() => setDeliverMode('generate')}>
                    AI voice
                  </Button>
                  <Button
                    tooltip="Read it and record it yourself"
                    active={isRecordMode}
                    data-testid="voice-mode-record"
                    icon={<Media.Microphone01 className="size-4" />}
                    onClick={() => setDeliverMode('record')}>
                    My voice
                  </Button>
                </ButtonGroup>
              </div>
            )}

            {!isRecordMode && (
              <>
                <SeparatorWithPlusIcon />
                <AppendClipSelect
                  selectedFileId={selectedFileId}
                  setSelectedFileId={setSelectedFileId}
                  playingPreviewClipId={playingPreviewClipId}
                  setPlayingPreviewClipId={setPlayingPreviewClipId}
                  audioDropFiles={audioDropFiles}
                  disabled={isGenerating || isSending}
                  previewAudioRef={previewAudioRef}
                />
              </>
            )}

            {isRecordMode && (
              <div className="flex flex-col gap-2 px-3 pb-3">
                {ttsMessage.trim().length > 0 && (
                  <div
                    className="rounded-md border border-red-200 bg-white/80 px-3 py-2 dark:border-red-800 dark:bg-red-950/60"
                    data-testid="voice-teleprompter">
                    <div className="mb-1 flex items-center justify-between">
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-red-500 dark:text-red-300">
                        Read this
                      </span>
                      {canWriteScripts && (
                        <Button
                          size="2xs"
                          text
                          color="red"
                          data-testid="voice-change-script"
                          onClick={() => setStep(scriptTakes.length > 0 ? 'script' : 'lead')}>
                          Change
                        </Button>
                      )}
                    </div>
                    <p className="text-[15px] leading-relaxed text-stone-800 dark:text-stone-100">{ttsMessage}</p>
                  </div>
                )}
                {!recordedAudioUrl && (
                  <RecorderComponent
                    onRecordingComplete={(audioBlob, durationMs) => {
                      // Store locally for preview instead of immediately uploading
                      const url = URL.createObjectURL(audioBlob);
                      setRecordedAudioUrl(url);
                      setRecordedAudioBlob({ blob: audioBlob, durationMs });
                    }}
                    // Cancel releases the mic (the recorder handles teardown) and drops back to
                    // AI voice instead of leaving the user in record mode with a live mic.
                    onCancel={() => setDeliverMode('generate')}
                    isUploading={isUploadingRecording}
                    disabled={isUploadingRecording}
                    // With a script on screen, let the rep read it first and start when ready.
                    autoStart={ttsMessage.trim().length === 0}
                  />
                )}
                {recordedAudioUrl && (
                  <div className="flex flex-col items-center gap-3">
                    <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
                      <Media.Microphone01 className="size-5" />
                      <span>Recording ready</span>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        color="red"
                        tone="soft"
                        size="sm"
                        onClick={() => {
                          if (playingPreviewClipId === 'recorded') {
                            previewAudioRef.current?.pause();
                            setPlayingPreviewClipId(null);
                          } else {
                            previewAudioRef.current?.pause();
                            const audio = new Audio(recordedAudioUrl);
                            previewAudioRef.current = audio;
                            audio.onended = () => setPlayingPreviewClipId(null);
                            audio.onerror = () => setPlayingPreviewClipId(null);
                            void audio.play().catch(() => {});
                            setPlayingPreviewClipId('recorded');
                          }
                        }}
                        icon={playingPreviewClipId === 'recorded' ? <Media.PauseCircle /> : <Media.PlayCircle />}>
                        {playingPreviewClipId === 'recorded' ? 'Pause' : 'Play'}
                      </Button>
                      <Button
                        color="red"
                        size="sm"
                        onClick={() => {
                          // Re-record
                          if (recordedAudioUrl) {
                            URL.revokeObjectURL(recordedAudioUrl);
                          }
                          setRecordedAudioUrl(null);
                          setRecordedAudioBlob(null);
                          setPlayingPreviewClipId(null);
                        }}
                        icon={<Media.Microphone01 />}>
                        Re-record
                      </Button>
                      <Button
                        color="green"
                        size="sm"
                        disabled={isUploadingRecording || isSending}
                        onClick={() => void handleSendRecording()}
                        icon={<General.Check />}>
                        {isUploadingRecording || isSending ? 'Sending...' : 'Send'}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Buttons below the bordered section — AI-voice delivery only */}
      {step === 'deliver' && !isRecordMode && (
        <div className="flex gap-2">
          <Button
            size="sm"
            onClick={handleGenerateAudio}
            disabled={isGenerating || isSending || !ttsMessage.trim()}
            className="flex-1 bg-indigo-600! text-white! hover:bg-indigo-700! active:bg-indigo-800! dark:bg-indigo-700! dark:hover:bg-indigo-800!"
            tooltip={
              voiceNoteStepId
                ? "Regenerate the audio. This updates the same draft — it doesn't create a duplicate send."
                : 'Generate audio for this voice note message'
            }>
            {isGenerating
              ? voiceNoteStepId
                ? 'Updating draft…'
                : 'Generating…'
              : voiceNoteStepId
                ? 'Update Draft'
                : 'Generate Audio'}
          </Button>
          {/* Play button when needs to listen first - like todo does */}
          {needsToPlayAudio && (
            <Button
              color="yellow"
              size="sm"
              onClick={() => playGeneratedAudio()}
              disabled={isGenerating || isSending}
              icon={playingPreviewClipId === 'generated' ? <Media.PauseCircle /> : <Media.PlayCircle />}
              tooltip="Listen to audio before sending">
              Listen
            </Button>
          )}
          {/* Play button for already played generated audio */}
          {!needsToPlayAudio && canPlayGenerated && (
            <Button
              color="blue"
              tone="soft"
              size="sm"
              onClick={() => playGeneratedAudio()}
              disabled={isGenerating || isSending}
              icon={playingPreviewClipId === 'generated' ? <Media.PauseCircle /> : <Media.PlayCircle />}
            />
          )}
          {/* Send/Schedule button - schedules the step for execution */}
          {voiceNoteStepId && (
            <Button
              color="yellow"
              size="sm"
              onClick={handleApproveVoiceNote}
              disabled={isGenerating || isSending || needsToPlayAudio}
              className="flex-1">
              {isSending ? 'Scheduling...' : needsToPlayAudio ? 'Listen first' : 'Schedule Voice Note'}
            </Button>
          )}
          {/* Play button for pre-recorded clips in dropdown */}
          {selectedFileId && !generatedAudioId && (
            <Button
              color="blue"
              tone="soft"
              size="sm"
              onClick={() => {
                const clip = audioDropFiles.find((f) => f.id === selectedFileId);
                if (clip?.url) {
                  if (playingPreviewClipId === selectedFileId) {
                    previewAudioRef.current?.pause();
                    setPlayingPreviewClipId(null);
                  } else {
                    previewAudioRef.current?.pause();
                    const audio = new Audio(clip.url);
                    previewAudioRef.current = audio;
                    audio.onended = () => setPlayingPreviewClipId(null);
                    audio.onerror = () => setPlayingPreviewClipId(null);
                    void audio.play().catch(() => {});
                    setPlayingPreviewClipId(selectedFileId);
                  }
                }
              }}
              disabled={isGenerating || isSending}
              icon={playingPreviewClipId === selectedFileId ? <Media.PauseCircle /> : <Media.PlayCircle />}
            />
          )}
        </div>
      )}
    </>
  );
}
